import { z } from 'zod';
import { RaidScale } from './raid.js';
/**
 * Tín hiệu rủi ro — APM-702, spec mục 12.1.
 *
 * ## Vì sao có tầng "tín hiệu" giữa dữ liệu và gợi ý AI
 *
 * Spec liệt kê sáu nguồn tín hiệu (lịch trình, workload, velocity, mô tả task,
 * tần suất CR, dữ liệu xuyên dự án). Việc **phát hiện** chúng là phép đếm và so
 * sánh — thứ code làm tất định, rẻ, và giải thích được từng con số. Việc **diễn
 * đạt** một tín hiệu thành rủi ro có xác suất và kế hoạch giảm thiểu thì cần
 * ngôn ngữ, và đó là phần AI làm.
 *
 * Tách hai tầng mua được đúng thứ ADR-006 đòi: bảng tín hiệu hiện đầy đủ **khi
 * chưa cấu hình key AI** — PM đọc nó rồi tự ghi rủi ro. AI là phần tăng tốc, và
 * nó không phải là chỗ duy nhất biết những con số này.
 */
export const RiskSignalSource = z.enum([
    /** Task trên đường găng gần như không còn buffer (`totalFloat` ≈ 0). */
    'critical_path_no_buffer',
    /** Nhiều task phụ thuộc dồn vào cùng một task/mốc. */
    'dependency_pileup',
    /** Một người bị cam kết quá khả năng (cộng dồn mọi dự án). */
    'workload_overload',
    /** Một mảng kỹ năng của dự án chỉ một người nắm. */
    'single_point_of_failure',
    /** Ước lượng thấp hơn đáng kể so với task cùng loại đã hoàn thành. */
    'estimate_below_history',
    /** Mô tả mơ hồ, còn "TBD", hoặc nhắc phụ thuộc bên ngoài. */
    'vague_description',
    /** Nhiều change request liên tiếp — dấu hiệu scope creep. */
    'cr_frequency',
    /** Loại việc tương tự từng gây vấn đề ở dự án khác ("trí nhớ tổ chức"). */
    'cross_project_memory',
    /**
     * Người phụ trách vắng mặt trong khoảng ngày của việc — APM-1312.
     *
     * Nghỉ phép, đã nghỉ việc, hoặc phần cam kết cho dự án này không đủ giờ. Ngày
     * nghỉ **lễ** cố tình không sinh tín hiệu: nó trừ giờ của cả đội như nhau, nên
     * một tín hiệu cho mỗi việc chạy qua tuần nghỉ Tết là bảng tín hiệu vô dụng.
     */
    'assignee_unavailable',
]);
/**
 * Một tín hiệu đã phát hiện.
 *
 * ⚠️ Không có trường câu-chữ nào. Tín hiệu mang **số và tên**, còn câu tiếng
 * Việt do frontend dựng từ `raid.signal.<source>` với `metrics` nội suy vào —
 * quy ước i18n của repo: server không sinh chuỗi hiển thị.
 */
export const RiskSignal = z.object({
    source: RiskSignalSource,
    /**
     * Mức đáng chú ý 1–5, để xếp thứ tự bảng tín hiệu. **Không** phải điểm rủi ro
     * chính thức: điểm đó là `probability × impact` của một RAID item do người
     * hoặc AI đặt ra, sau khi đã cân nhắc tín hiệu này.
     */
    weight: RaidScale,
    taskId: z.uuid().nullable(),
    /** `PRJ-12`. Có sẵn để PM bấm sang task, và để AI dẫn chứng bằng mã. */
    taskKey: z.string().nullable(),
    /** Tên người hoặc tên tech stack mà tín hiệu nói về, nếu có. */
    subject: z.string().nullable(),
    /** Con số của tín hiệu: ngày float, giờ vượt, số mẫu lịch sử… */
    metrics: z.record(z.string(), z.number()),
});
export const RiskSignalReport = z.object({
    projectId: z.uuid(),
    scannedAt: z.iso.datetime(),
    signals: z.array(RiskSignal),
    /**
     * Nguồn tín hiệu mà **hệ thống chưa có dữ liệu để dò**, kèm mã task sẽ mở nó.
     *
     * Hiện ra thay vì im lặng bỏ qua: một bảng tín hiệu thiếu hai nguồn mà không
     * nói gì sẽ được đọc là "dự án này không có rủi ro loại đó".
     */
    unavailable: z.array(z.object({ source: RiskSignalSource, blockedBy: z.string() })),
});
// ── Gợi ý rủi ro do AI sinh ─────────────────────────────────────────────────
/**
 * Hình dạng `ai_suggestion.content` của skill `risk_scan`.
 *
 * `source` và `evidence` bắt buộc: ADR-005 nói gợi ý không giải thích được căn
 * cứ thì PM không có cơ sở để duyệt và sẽ bỏ qua tất cả. Spec 12.1 cũng đòi nêu
 * "nguồn phát hiện".
 */
export const RiskSuggestionItem = z.object({
    title: z.string().min(1).max(300),
    description: z.string().max(2_000),
    probability: RaidScale,
    impact: RaidScale,
    mitigation: z.string().max(2_000).nullable(),
    source: RiskSignalSource,
    /** Dẫn chứng bằng số hoặc mã task — thứ PM đối chiếu được với bảng tín hiệu. */
    evidence: z.string().min(1).max(500),
    /** `PRJ-12` nếu rủi ro nói về một task cụ thể. */
    relatedTaskKey: z.string().max(40).nullable(),
});
export const RiskSuggestionOutput = z.object({
    /**
     * Rỗng là kết quả **hợp lệ**: "đã đọc tín hiệu, không có rủi ro mới nào đáng
     * ghi". Bắt tối thiểu một phần tử thì model buộc phải bịa ra một rủi ro chung
     * chung để thoả schema — đúng thứ prompt đang cấm nó làm.
     */
    risks: z.array(RiskSuggestionItem).max(8),
});
/**
 * Duyệt gợi ý: PM chọn **một phần** danh sách.
 *
 * Duyệt trọn gói là cách nhanh nhất để bảng RAID đầy rủi ro không ai đọc. Mục
 * 12.1 nói thẳng nỗi lo đó: "tránh gợi ý tràn lan gây phiền".
 */
export const AcceptRiskSuggestionInput = z.object({
    /** Chỉ số trong `content.risks` mà PM muốn ghi thành rủi ro thật. */
    indexes: z.array(z.number().int().min(0).max(7)).min(1).max(8),
});
export const DismissRiskSuggestionInput = z.object({
    /**
     * Bắt buộc. Lý do bỏ qua là dữ liệu để cải thiện gợi ý về sau (spec 12.1) —
     * optional thì không ai điền, và ta mất luôn tín hiệu học được duy nhất.
     */
    reason: z.string().min(3).max(500),
});
//# sourceMappingURL=risk-signal.js.map